// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Receipt, Plus, User, AlertCircle, CheckCircle, CreditCard, Zap, Printer, Download, Search, PenLine } from 'lucide-react'
import { formatBaht } from '@/lib/money'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, SetupGuide, Skeleton, Select, Input, Modal, Pagination, CardKebabMenu, ConfirmDialog, DataTable, Checkbox } from '@/components/ui'
import type { Column } from '@/components/ui'
import { MobileSortControl } from '@/components/ui/MobileSortControl'
import { PeriodAffordanceBanner } from '@/components/ui/PeriodAffordanceBanner'
import { useListSort } from '@/hooks/useListSort'
import { useSupportSession } from '@/hooks/useSupportSession'
import { ExportModal } from '@/components/export/ExportModal'
import { fetchAllInvoices } from '@/components/export/exportUtils'
import { useInvoices, useSignInvoice, useDeleteInvoice, fetchInvoice } from '@/hooks/useInvoices'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useDateFormatter, useLocalizationConfig } from '@/hooks/useDateFormatter'
import { useDocumentTemplates } from '@/hooks/useDocumentTemplates'
import { useLeases } from '@/hooks/useLeases'
import { useProperties } from '@/hooks/useProperties'
import { useSelectedPropertyStore } from '@/stores'
import type { Invoice } from '@/types'
// WEB-H7: PDF generators are dynamic-imported at the call sites (pdfme weight).
import { generateBillingPeriods, getMonthRange } from '@/lib/billingPeriod'
import type { Template } from '@pdfme/common'
import { useFeatureFlags } from '@/lib/featureFlags'
import { useAuth } from '@/hooks/useAuth'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { SignInvoiceModal } from '@/components/invoices/SignInvoiceModal'
import { InvoiceStatusBadge } from '@/components/invoices/InvoiceStatusBadge'

export function InvoiceListPage() {
  const { t, i18n } = useTranslation('invoices')
  const { t: tc } = useTranslation('common')
  const supportSession = useSupportSession().active
  const navigate = useNavigate()
  const { calendar } = useLocalizationConfig()
  const featureFlags = useFeatureFlags()
  const { user } = useAuth()
  const addToast = useNotificationStore((s) => s.addToast)
  const isAdminOrOwner = user?.role === 'admin' || user?.role === 'owner'
  const [page, setPage] = useState(1)
  const [status, setStatus] = useState('')
  const [billingPeriod, setBillingPeriod] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  // EP-97: rent-roll page — the list defaults to the latest billing period with
  // rows. `allPeriods` sends all_periods=true to escape that default (set when the
  // user picks "All periods" or clicks the affordance escape).
  const [allPeriods, setAllPeriods] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [showPrintModal, setShowPrintModal] = useState(false)
  const [isPrinting, setIsPrinting] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)
  const [showSignModal, setShowSignModal] = useState(false)
  const [signProgress, setSignProgress] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  // TC-MUI-A6: mobile collapsible state for the secondary filters (search stays
  // outside the collapse). The badge counts active secondary filters.
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false)
  const selectedPropertyId = useSelectedPropertyStore((s) => s.selectedPropertyId)
  const setSelectedPropertyId = useSelectedPropertyStore((s) => s.setSelectedPropertyId)
  const { data: propertiesData } = useProperties(1)
  const properties = propertiesData?.data ?? []
  const [pendingDelete, setPendingDelete] = useState<Invoice | null>(null)
  const deleteInvoice = useDeleteInvoice()

  const signInvoice = useSignInvoice()

  const handleBulkSign = async (signatureDataUrlOrKey: string) => {
    const ids = Array.from(selectedIds)
    const template = invoiceTemplates?.data?.[0]?.template as Template | undefined
    if (!template) {
      addToast({ type: 'error', message: tc('errors.something_went_wrong') })
      return
    }

    const signerName = user?.full_name || user?.email || 'Signer'
    let signedCount = 0
    try {
      const { generateAndUploadSignedInvoicePdf } = await import('@/lib/invoicePdfGenerator')
      for (const id of ids) {
        const progressMessage = t('sign_modal.progress', { current: signedCount + 1, total: ids.length })
        setSignProgress(progressMessage)
        addToast({ type: 'info', message: progressMessage })
        const invoice = await fetchInvoice(id)
        const key = await generateAndUploadSignedInvoicePdf(invoice, template, signatureDataUrlOrKey, signerName, accountSettings)
        await signInvoice.mutateAsync({ id, signed_pdf_key: key })
        signedCount++
      }
      addToast({ type: 'success', message: t('bulk.sign_success', { count: signedCount }) })
      setSelectedIds(new Set())
      setShowSignModal(false)
    } catch {
      if (signedCount > 0) {
        addToast({ type: 'warning', message: t('bulk.sign_partial', { count: signedCount, total: ids.length }) })
      } else {
        addToast({ type: 'error', message: tc('errors.something_went_wrong') })
      }
    } finally {
      setSignProgress(null)
    }
  }

  // EP-97: rent-roll default sort = unit number ascending; user-selectable
  // server-side column sort. billed_at/due_at/total/number also allow-listed.
  const { sort, order, setSort, toggleOrder, sortParams } = useListSort({
    defaultSort: 'unit_number',
    defaultOrder: 'asc',
  })
  const handleSort = (key: string) => { setPage(1); setSort(key) }
  const handleToggleOrder = () => { setPage(1); toggleOrder() }

  const { data, isLoading, isFetching, error } = useInvoices(
    page,
    status || undefined,
    undefined,
    dateFrom || undefined,
    dateTo || undefined,
    undefined,
    searchQuery || undefined,
    selectedPropertyId ?? undefined,
    { ...sortParams, allPeriods },
  )

  const sortOptions = [
    { value: 'unit_number', label: tc('sort.by_unit') },
    { value: 'number', label: t('labels.invoice_number') },
    { value: 'due_at', label: t('labels.due_date') },
    { value: 'total', label: t('labels.total') },
    { value: 'billed_at', label: t('labels.billing_period') },
  ]

  // EP-97: format the resolved default period for the affordance banner. The
  // banner renders nothing when source === 'explicit' (user chose a period).
  const defaultPeriod = data?.meta?.default_period

  const { data: accountSettings } = useAccountSettings()
  const formatDate = useDateFormatter()
  const { data: invoiceTemplates } = useDocumentTemplates(1, 'invoice')

  const activeFilterCount = [billingPeriod, status, dateFrom, dateTo].filter(Boolean).length

  // Check prerequisites for setup guide
  const { data: leasesData } = useLeases(1, 'active')
  const hasActiveLeases = (leasesData?.data?.length ?? 0) > 0

  // Clear selection when page/filters change
  useEffect(() => {
    setSelectedIds(new Set())
  }, [page, status, billingPeriod, dateFrom, dateTo])

  const invoices = data?.data || []

  const billingPeriodOptions = [
    { value: '', label: t('filters.all_periods') },
    ...generateBillingPeriods({ locale: i18n.language, calendar }),
    { value: 'custom', label: t('filters.custom_range') },
  ]

  const handleBillingPeriodChange = (value: string) => {
    setBillingPeriod(value)
    if (value && value !== 'custom') {
      const { from, to } = getMonthRange(value)
      setDateFrom(from)
      setDateTo(to)
      setAllPeriods(false)
    } else if (value === '') {
      // Explicitly choosing "All periods" opts out of the latest-period default.
      setDateFrom('')
      setDateTo('')
      setAllPeriods(true)
    } else {
      // 'custom' — user will pick an explicit range below.
      setAllPeriods(false)
    }
    setPage(1)
  }

  // EP-97: affordance escape — jump from the default latest-period view to all periods.
  const handleShowAllPeriods = () => {
    setBillingPeriod('')
    setDateFrom('')
    setDateTo('')
    setAllPeriods(true)
    setPage(1)
  }
  // EP-97 fix (2026-08-19): the affordance used to live only in the has-rows
  // branch, so when the default billing-cycle window matched nothing the user
  // got an empty list with no hint that a period filter was applied and no way
  // out — exactly when the escape is needed most. Rendered in BOTH branches
  // now; the component self-hides when the caller chose an explicit period.
  const periodBanner = defaultPeriod ? (
    <PeriodAffordanceBanner
      period={defaultPeriod}
      formattedPeriod={defaultPeriod.from ? formatDate(defaultPeriod.from) : ''}
      onShowAllPeriods={handleShowAllPeriods}
    />
  ) : null

  const statusOptions = [
    { value: '', label: t('status.all') },
    { value: 'unpaid', label: t('status.unpaid') },
    { value: 'partially_paid', label: t('status.partially_paid') },
    { value: 'paid', label: t('status.paid') },
    { value: 'overdue', label: t('status.overdue') },
  ]

  const getStatusIcon = (invoice: Invoice) => {
    if (invoice.status === 'paid') return <CheckCircle className="w-4 h-4 text-success" />
    if (invoice.status === 'overdue') return <AlertCircle className="w-4 h-4 text-error" />
    if (invoice.status === 'partially_paid') return <CreditCard className="w-4 h-4 text-info" />
    return <Receipt className="w-4 h-4 text-warning" />
  }

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const toggleSelectAll = () => {
    if (selectedIds.size === invoices.length) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(invoices.map((inv) => inv.id)))
    }
  }

  const handlePrint = async () => {
    const tmpl = invoiceTemplates?.data?.[0]?.template as Template | undefined
    if (!tmpl) return
    setIsPrinting(true)
    try {
      // Always fetch with items included for PDF generation
      // MON-18 C: the print PDF follows the header's selected building, same
      // as the on-screen list and the CSV export.
      const selectedInvoices = await fetchAllInvoices(
        dateFrom || undefined,
        dateTo || undefined,
        status || undefined,
        true, // include_items for PDF
        selectedPropertyId ?? undefined
      )
      // Filter to selected if any
      const toPrint = selectedIds.size > 0
        ? selectedInvoices.filter((inv) => selectedIds.has(inv.id))
        : selectedInvoices
      const { generateMultiInvoicePdf } = await import('@/lib/invoicePdfGenerator')
      const pdf = await generateMultiInvoicePdf(toPrint, tmpl, accountSettings)
      const blob = new Blob([pdf.buffer as ArrayBuffer], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
      setShowPrintModal(false)
    } finally {
      setIsPrinting(false)
    }
  }

  const allSelected = invoices.length > 0 && selectedIds.size === invoices.length

  // R008b: e-Tax Invoice column is feature-flagged, so it's built separately and
  // spread into `columns` only when active (keeps the array's element type a
  // concrete `Column<Invoice>` rather than a union that trips contextual typing
  // inside a conditional array literal).
  const taxInvoiceColumn: Column<Invoice> | null = featureFlags.eTaxInvoice
    ? {
        key: 'tax_invoice_number',
        header: t('tax_invoice.number'),
        hideBelow: 'lg',
        className: 'text-sm text-base-content/60',
        render: (invoice) => invoice.tax_invoice_number || '-',
      }
    : null

  // OWNER DECISION 2026-07-24: DataTable's Column.header is a plain string, so it
  // cannot host the select-all header checkbox. Per-row checkboxes stay as a
  // render column (stopPropagation so the click doesn't also trigger row
  // navigation, mirroring PaymentListPage's actions column); select-all moved to
  // the bulk toolbar below.
  const columns: Column<Invoice>[] = [
    {
      key: 'select',
      header: '',
      className: 'w-10',
      render: (invoice) => (
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            size="sm"
            checked={selectedIds.has(invoice.id)}
            onChange={() => toggleSelect(invoice.id)}
          />
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-10',
      render: (invoice) => (
        <div onClick={(e) => e.stopPropagation()}>
          <CardKebabMenu
            ariaLabel={tc('labels.actions_menu')}
            items={[
              { label: tc('actions.view'), onClick: () => navigate(`/invoices/${invoice.id}`) },
              { label: tc('actions.edit'), onClick: () => navigate(`/invoices/${invoice.id}/edit`) },
              { label: tc('actions.delete'), variant: 'error', onClick: () => setPendingDelete(invoice) },
            ]}
          />
        </div>
      ),
    },
    {
      key: 'number',
      header: t('labels.invoice_number'),
      sortable: true,
      render: (invoice) => (
        <div className="flex items-center gap-2">
          {getStatusIcon(invoice)}
          <span className="font-medium truncate max-w-[120px] inline-block">{invoice.number}</span>
        </div>
      ),
    },
    {
      key: 'status',
      header: tc('labels.status'),
      render: (invoice) => (
        <div className="flex flex-wrap gap-1">
          <InvoiceStatusBadge invoice={invoice} />
          {invoice.has_missing_slip && (
            <Badge variant="error" size="sm">{t('payment.missing_slip')}</Badge>
          )}
        </div>
      ),
    },
    {
      key: 'total',
      header: t('labels.total'),
      sortable: true,
      className: 'text-right',
      render: (invoice) => (
        <span className="font-medium whitespace-nowrap">{formatBaht(invoice.total, { decimals: 2 })}</span>
      ),
    },
    {
      key: 'balance_due',
      header: t('labels.balance_due'),
      hideBelow: 'md',
      className: 'text-right',
      render: (invoice) => (
        <span className={`font-medium whitespace-nowrap ${invoice.balance_due > 0 ? 'text-warning' : ''}`}>
          {formatBaht(invoice.balance_due, { decimals: 2 })}
        </span>
      ),
    },
    {
      key: 'customer',
      header: t('labels.customer'),
      render: (invoice) => invoice.customer ? (
        <div className="flex items-center gap-1">
          <User className="w-3 h-3 text-base-content/40" />
          <span>{invoice.customer.name}</span>
        </div>
      ) : '-',
    },
    {
      key: 'unit_number',
      header: t('labels.unit'),
      sortable: true,
      render: (invoice) => invoice.unit?.name || '-',
    },
    {
      key: 'due_at',
      header: t('labels.due_date'),
      sortable: true,
      hideBelow: 'md',
      className: 'whitespace-nowrap',
      render: (invoice) => formatDate(invoice.due_at),
    },
    ...(taxInvoiceColumn ? [taxInvoiceColumn] : []),
  ]

  return (
    <>
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: t('title') }]} />

        <PageHeader
          title={t('title')}
          description={t('description')}
          isRefreshing={isFetching && !isLoading}
          titleAction={
            // TC-MUI-A5: compact property selector pinned top-right of the page
            // header on MOBILE (PageHeader hides this on sm+). Synced with the
            // same store as the desktop top-bar switcher + the in-body card.
            <Select
              data-testid="page-property-picker-mobile"
              aria-label={tc('labels.select_property')}
              value={selectedPropertyId ? String(selectedPropertyId) : ''}
              onChange={(e) => {
                setSelectedPropertyId(e.target.value ? Number(e.target.value) : null)
                setPage(1)
              }}
              options={[
                { value: '', label: tc('labels.all_properties') },
                ...properties.map((p) => ({ value: String(p.id), label: p.name })),
              ]}
              className="max-w-[9rem]"
              size="sm"
            />
          }
          primaryAction={{
            key: 'new-invoice',
            label: t('actions.new_invoice'),
            icon: Plus,
            onClick: () => navigate('/invoices/new'),
          }}
          overflowActions={[
            {
              key: 'generate',
              label: t('actions.generate'),
              icon: Zap,
              onClick: () => navigate('/invoices/generate'),
            },
            {
              key: 'print',
              label: selectedIds.size > 0
                ? t('actions.print_selected', { count: selectedIds.size })
                : t('actions.print_all'),
              icon: Printer,
              onClick: () => setShowPrintModal(true),
            },
            {
              key: 'export',
              label: tc('export_data.export_button'),
              icon: Download,
              onClick: () => setShowExportModal(true),
              // EP-160 S3: no CSV export in a FlowRent support session.
              hidden: supportSession,
            },
          ]}
        />


        {/* Filters.
            TC-MUI-A6: the search input stays OUTSIDE the collapse (always
            visible on mobile); only the secondary filters (billing period,
            status, date range) hide behind the `Filters [n] ▾` toggle. From
            `sm` up everything renders inline as before. */}
        <Card>
          {/* Search — always visible (min-w-0 so it can shrink, TC-MUI-A7) */}
          <div className="w-full min-w-0">
            <Input
              label={tc('actions.search')}
              leftIcon={<Search className="w-4 h-4" />}
              placeholder={t('filters.search_placeholder')}
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(1) }}
            />
          </div>

          {/* Mobile-only toggle for the secondary filters */}
          <button
            type="button"
            className="sm:hidden flex items-center justify-between w-full cursor-pointer select-none font-medium mt-3"
            aria-expanded={mobileFiltersOpen}
            aria-controls="invoice-filters-body"
            onClick={() => setMobileFiltersOpen((v) => !v)}
          >
            <span>
              {tc('filters.toggle')}
              {activeFilterCount > 0 && (
                <Badge variant="count" size="sm" className="ml-2">{activeFilterCount}</Badge>
              )}
            </span>
            <span className={`text-base-content/60 transition-transform duration-200 ${mobileFiltersOpen ? 'rotate-180' : ''}`}>▾</span>
          </button>

          <div
            id="invoice-filters-body"
            className={`flex-wrap gap-4 items-end mt-3 ${mobileFiltersOpen ? 'flex' : 'hidden sm:flex'}`}
          >
            {/* EP-97: mobile sort control — desktop sorts via clickable table headers. */}
            <div className="w-full sm:hidden">
              <MobileSortControl
                options={sortOptions}
                value={sort}
                order={order}
                onChange={handleSort}
                onToggleOrder={handleToggleOrder}
              />
            </div>
            <Select
              label={t('labels.billing_period')}
              value={billingPeriod}
              onChange={(e) => handleBillingPeriodChange(e.target.value)}
              options={billingPeriodOptions}
              className="w-full sm:w-52 min-w-0"
            />
            <Select
              id="invoice-status-filter"
              label={tc('labels.status')}
              value={status}
              onChange={(e) => {
                setStatus(e.target.value)
                setPage(1)
              }}
              options={statusOptions}
              className="w-full sm:w-48 min-w-0"
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full sm:flex-1 sm:min-w-[280px]">
              <Input
                label={t('filters.date_from')}
                type="date"
                value={dateFrom}
                onChange={(e) => {
                  setDateFrom(e.target.value)
                  setPage(1)
                }}
                disabled={billingPeriod !== '' && billingPeriod !== 'custom'}
              />
              <Input
                label={t('filters.date_to')}
                type="date"
                value={dateTo}
                onChange={(e) => {
                  setDateTo(e.target.value)
                  setPage(1)
                }}
                disabled={billingPeriod !== '' && billingPeriod !== 'custom'}
              />
            </div>
          </div>
        </Card>

        {/* Invoice Table */}
        {isLoading ? (
          <Card>
            <div className="space-y-4">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12" />
              ))}
            </div>
          </Card>
        ) : error ? (
          <Card>
            <EmptyState
              title={t('messages.error_loading')}
              description={t('messages.error_description')}
              action={<Button onClick={() => window.location.reload()}>{tc('actions.retry')}</Button>}
            />
          </Card>
        ) : !invoices.length ? (
          <Card>
            {periodBanner}
            {/* B-017: only show the first-run SetupGuide when the account truly
                has NO invoices AND no filter is narrowing the view. If invoices
                exist but the active filters (property/status/dates) match none,
                show a "no match — clear filters" state so existing invoices are
                never hidden behind a misleading "start issuing" prompt. */}
            {(accountSettings?.invoices_count ?? 0) === 0 &&
            !status &&
            !dateFrom &&
            !dateTo &&
            !selectedPropertyId ? (
              <SetupGuide
                icon={<Receipt className="w-16 h-16" />}
                title={t('setup_guide.title')}
                description={t('setup_guide.description')}
                prerequisites={[
                  {
                    label: t('setup_guide.prereq_lease'),
                    met: hasActiveLeases,
                    link: '/leases',
                    linkLabel: t('setup_guide.prereq_setup_lease'),
                  },
                ]}
                prerequisitesTitle={t('setup_guide.prerequisites_title')}
                steps={[
                  { title: t('setup_guide.step_1_title'), description: t('setup_guide.step_1_description') },
                  { title: t('setup_guide.step_2_title'), description: t('setup_guide.step_2_description') },
                  { title: t('setup_guide.step_3_title'), description: t('setup_guide.step_3_description') },
                ]}
                action={
                  <div className="flex flex-col items-center gap-3">
                    <div className="flex gap-2">
                      <Button
                        leftIcon={<Plus className="w-4 h-4" />}
                        onClick={() => navigate('/invoices/new')}
                        disabled={!hasActiveLeases}
                      >
                        {t('actions.new_invoice')}
                      </Button>
                      <Button
                        variant="outline"
                        leftIcon={<Zap className="w-4 h-4" />}
                        onClick={() => navigate('/invoices/generate')}
                        disabled={!hasActiveLeases}
                      >
                        {t('actions.generate')}
                      </Button>
                    </div>
                    <button
                      type="button"
                      className="text-sm text-base-content/60 hover:text-primary transition-colors underline-offset-2 hover:underline"
                      onClick={() => navigate('/onboarding?step=7&mode=sample')}
                    >
                      {t('setup_guide.preview_sample')}
                    </button>
                  </div>
                }
              />
            ) : (
              <EmptyState
                icon={<Receipt className="w-12 h-12" />}
                title={t('empty.no_match_title')}
                description={t('empty.no_match_description')}
                action={
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        // Clear every filter — incl. the (account-global)
                        // property selector — so all invoices reappear.
                        setStatus('')
                        setBillingPeriod('')
                        setDateFrom('')
                        setDateTo('')
                        setSelectedPropertyId(null)
                        // The period default is applied SERVER-side when no
                        // from/to/all_periods is sent, so clearing the visible
                        // filters alone leaves the same window in force and the
                        // button appears to do nothing.
                        setAllPeriods(true)
                        setPage(1)
                      }}
                    >
                      {t('empty.clear_filters')}
                    </Button>
                    <Button leftIcon={<Plus className="w-4 h-4" />} onClick={() => navigate('/invoices/new')}>
                      {t('actions.new_invoice')}
                    </Button>
                  </div>
                }
              />
            )}
          </Card>
        ) : (
          <Card>
            {/* EP-97: rent-roll period affordance — hidden when the user chose an
                explicit period (source === 'explicit'). */}
            {periodBanner}
            {/* Bulk action toolbar — always visible so select-all is reachable
                without a header checkbox (DataTable's Column.header is a plain
                string, OWNER DECISION 2026-07-24). The leading checkbox doubles
                as "select all" / "some selected" indicator; its label switches
                to the selected count once at least one row is checked. */}
            <div className="flex items-center gap-3 mb-4 px-1 py-2 bg-base-200 rounded-lg flex-wrap">
              <label className="flex items-center gap-2 text-sm font-medium text-base-content/70 cursor-pointer">
                <Checkbox
                  size="sm"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  aria-label={t('bulk.select_all')}
                />
                {selectedIds.size > 0 ? t('bulk.selected_count', { count: selectedIds.size }) : t('bulk.select_all')}
              </label>
              {selectedIds.size > 0 && (
                <>
                  {isAdminOrOwner && (
                    <Button
                      size="sm"
                      leftIcon={<PenLine className="w-4 h-4" />}
                      onClick={() => setShowSignModal(true)}
                    >
                      {t('bulk.sign')}
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSelectedIds(new Set())}
                  >
                    {t('bulk.clear_selection')}
                  </Button>
                </>
              )}
            </div>
            <DataTable
              size="sm"
              columns={columns}
              data={invoices}
              keyExtractor={(invoice) => invoice.id}
              onRowClick={(invoice) => navigate(`/invoices/${invoice.id}`)}
              sortBy={sort}
              sortOrder={order}
              onSort={handleSort}
            />
          </Card>
        )}

        {/* Pagination */}
        {data?.meta && data.meta.total_count > 0 && (
          <div className="flex justify-center">
            <Pagination
              currentPage={data.meta.current_page}
              totalPages={data.meta.total_pages}
              totalItems={data.meta.total_count}
              pageSize={data.meta.per_page}
              onPageChange={setPage}
            />
          </div>
        )}
      </div>

      {/* Print Modal */}
      <Modal
        isOpen={showPrintModal}
        onClose={() => setShowPrintModal(false)}
        title={t('print.title')}
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowPrintModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button onClick={handlePrint} loading={isPrinting}>
              {isPrinting ? t('print.generating') : t('print.generate')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-base-content/60">
          {selectedIds.size > 0
            ? t('actions.print_selected', { count: selectedIds.size })
            : t('actions.print_all_description', { count: data?.meta?.total_count ?? 0 })}
        </p>
      </Modal>

      {/* Sign Invoice Modal */}
      <SignInvoiceModal
        isOpen={showSignModal}
        onClose={() => { setShowSignModal(false); setSignProgress(null) }}
        onSign={handleBulkSign}
        loading={signInvoice.isPending || signProgress !== null}
        title={`${t('bulk.sign')} (${selectedIds.size})`}
      />

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        dataType="invoices"
        dateFrom={dateFrom || undefined}
        dateTo={dateTo || undefined}
        status={status || undefined}
      />

      <ConfirmDialog
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={async () => {
          if (!pendingDelete) return
          await deleteInvoice.mutateAsync(pendingDelete.id)
          setPendingDelete(null)
        }}
        title={tc('actions.delete')}
        message={t('messages.delete_confirm')}
        confirmLabel={tc('actions.delete')}
        cancelLabel={tc('actions.cancel')}
        variant="error"
        loading={deleteInvoice.isPending}
      />
    </>
  )
}
